import { SEO } from "@/components/SEO";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { CheckCircle, Wrench, Shield, Clock, Phone, Users } from "lucide-react";
import { Link } from "react-router-dom";
import { ModernLogo } from "@/components/ModernLogo";
import plumberImage from "@/assets/real-plumber.jpg";

export const PlumbingPage = () => {
  return (
    <div className="min-h-screen bg-gradient-subtle">
      <SEO
        title="Professional Plumbing Services - Emergency Plumbers | Sortable"
        description="Find qualified emergency plumbers and heating engineers near you. 24/7 plumbing repairs, installations, and maintenance across the UK. Fully insured and certified professionals."
        keywords="plumber, emergency plumber, plumbing services, heating engineer, boiler repair, leak repair, tap installation, bathroom fitting, central heating, gas safe, pipe repair, drain unblocking, water heater repair, plumbing maintenance, emergency callout, 24/7 plumber, local plumber, qualified plumber, certified plumber, insured plumber"
        canonical="https://sortable.lovable.app/services/plumbing"
        ogImage="https://sortable.lovable.app/real-plumber.jpg"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "Service",
          "name": "Professional Plumbing Services",
          "description": "Emergency plumbing and heating services by qualified professionals",
          "provider": {
            "@type": "Organization",
            "name": "Sortable"
          },
          "serviceType": "Plumbing Services",
          "areaServed": {
            "@type": "Country",
            "name": "United Kingdom"
          }
        }}
      />

      {/* Header */}
      <header className="bg-background/95 backdrop-blur-sm border-b border-border/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-2 md:py-3">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2">
              <ModernLogo showTagline={false} />
            </Link>
            <div className="flex items-center gap-1 md:gap-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <span className="hidden md:inline">Home</span>
                  <span className="md:hidden">Home</span>
                </Button>
              </Link>
              <Link to="/for-tradesmen">
                <Button variant="outline" size="sm">
                  <span className="hidden md:inline">For Tradesmen</span>
                  <span className="md:hidden">Tradesmen</span>
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-12 px-4">
        <div className="container mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-blue-600/10 border border-blue-600/20 text-blue-600 px-6 py-3 rounded-full text-lg font-semibold mb-8 shadow-elegant">
            <Wrench className="h-5 w-5" />
            Professional Plumbing Services
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-hero bg-clip-text text-transparent">
            Expert Plumbers & Heating Engineers
          </h1>
          <p className="text-xl text-muted-foreground mb-8 max-w-3xl mx-auto">
            From emergency repairs to complete bathroom installations, find qualified, insured plumbers and heating engineers ready to solve your plumbing problems quickly and professionally.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
            <Link to="/create-job">
              <Button size="lg" className="bg-gradient-hero hover:shadow-glow transform hover:scale-105">
                <Users className="mr-2 h-5 w-5" />
                Find Plumbers Now
              </Button>
            </Link>
            <Link to="/for-tradesmen">
              <Button size="lg" variant="outline">
                <Wrench className="mr-2 h-5 w-5" />
                Join as a Plumber
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-16 px-4 bg-background/50">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Comprehensive Plumbing Services
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Our network of qualified plumbers provides complete plumbing solutions for residential and commercial properties.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                title: "Emergency Repairs",
                description: "24/7 emergency plumbing repairs for burst pipes, leaks, and urgent issues",
                icon: <Clock className="w-6 h-6" />
              },
              {
                title: "Bathroom Installation",
                description: "Complete bathroom fitting including toilets, sinks, showers, and baths",
                icon: <Wrench className="w-6 h-6" />
              },
              {
                title: "Heating Systems",
                description: "Boiler installation, repair, and central heating system maintenance",
                icon: <Shield className="w-6 h-6" />
              },
              {
                title: "Leak Detection & Repair",
                description: "Professional leak detection and repair services for all pipe systems",
                icon: <CheckCircle className="w-6 h-6" />
              },
              {
                title: "Drain Unblocking",
                description: "Drain cleaning and unblocking services for kitchens and bathrooms",
                icon: <Wrench className="w-6 h-6" />
              },
              {
                title: "Gas Safety Certificates",
                description: "Gas safety inspections and certificates by Gas Safe registered engineers",
                icon: <Shield className="w-6 h-6" />
              }
            ].map((service, index) => (
              <Card key={index} className="border-border/50 hover:shadow-elegant transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg text-primary">
                      {service.icon}
                    </div>
                    <CardTitle className="text-lg">{service.title}</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Section */}
      <section className="py-16 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose Our Plumbing Network?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              All our plumbers are thoroughly vetted, fully insured, and committed to providing excellent service.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Qualified & Certified</h4>
                    <p className="text-muted-foreground">
                      All plumbers hold relevant qualifications and certifications, including Gas Safe registration where applicable.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Fully Insured</h4>
                    <p className="text-muted-foreground">
                      Every professional carries comprehensive public liability insurance for your peace of mind.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">24/7 Emergency Service</h4>
                    <p className="text-muted-foreground">
                      Emergency plumbers available around the clock for urgent repairs and callouts.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <CheckCircle className="h-6 w-6 text-green-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-foreground mb-2">Competitive Pricing</h4>
                    <p className="text-muted-foreground">
                      Get multiple quotes from local plumbers to ensure you receive the best value for money.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src={plumberImage} 
                alt="Professional plumber working on pipes"
                className="rounded-lg shadow-lg w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-primary text-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Need a Plumber Today?
            </h2>
            <p className="text-xl mb-8 text-white/90">
              Post your plumbing job and get quotes from qualified, local professionals. Fast, reliable, and fully insured service guaranteed.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/create-job">
                <Button 
                  size="lg" 
                  variant="outline"
                  className="bg-white text-primary hover:bg-gray-100 font-semibold px-8 py-3 border-white"
                >
                  <Users className="mr-2 h-5 w-5" />
                  Get Quotes Now
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-background border-t py-12">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
            <div>
              <ModernLogo showTagline={false} />
              <p className="text-muted-foreground mt-4 max-w-md">
                Connecting you with qualified, insured plumbers across the UK for all your plumbing needs.
              </p>
            </div>
            <div>
              <h4 className="font-semibold mb-4">Services</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li>Emergency Repairs</li>
                <li>Bathroom Installation</li>
                <li>Heating Systems</li>
                <li>Leak Detection</li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Customers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/" className="hover:text-primary">Home</Link></li>
                <li><Link to="/how-it-works-customer" className="hover:text-primary">How It Works</Link></li>
                <li><Link to="/create-job" className="hover:text-primary">Post a Job</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-semibold mb-4">For Plumbers</h4>
              <ul className="space-y-2 text-muted-foreground">
                <li><Link to="/for-tradesmen" className="hover:text-primary">Join Our Network</Link></li>
                <li><Link to="/tradesman-registration" className="hover:text-primary">Register Now</Link></li>
                <li><Link to="/how-it-works" className="hover:text-primary">How It Works</Link></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};